import { motion } from "framer-motion";
import { useTranslation } from "../i18n/LanguageContext";

export default function GlobeSection() {
  const { t, lang } = useTranslation();
  const usLatinoLabel = lang === "es" ? "MERCADO LATINO" : "US LATINO MARKET";

  return (
    <section
      id="locations"
      className="py-24 md:py-32 bg-[#0A0A0B] relative overflow-hidden"
      data-testid="globe-section"
    >
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-[#C9A96E]/5 blur-[180px] rounded-full pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="mb-16 text-center"
          data-testid="globe-header"
        >
          <div className="flex items-center justify-center gap-4 mb-6">
            <motion.span
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="inline-block w-10 h-px bg-[#C9A96E]"
              style={{ transformOrigin: "center" }}
            />
            <span className="fwd-eyebrow">{t.globe.eyebrow}</span>
            <motion.span
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              className="inline-block w-10 h-px bg-[#C9A96E]"
              style={{ transformOrigin: "center" }}
            />
          </div>
          <h2
            className="fwd-heading mx-auto"
            style={{ maxWidth: "880px" }}
            data-testid="globe-headline"
          >
            {t.globe.headlineLead}{" "}
            <span className="fwd-italic-gold">{t.globe.headlineItalic}</span>
          </h2>
          <p
            className="fwd-body mt-6 mx-auto"
            style={{ maxWidth: "680px" }}
          >
            {t.globe.subtitle}
          </p>
        </motion.div>

        {/* Globe graphic */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: "easeOut" }}
          className="flex justify-center"
          data-testid="globe-container"
        >
          <div className="w-full max-w-[640px] aspect-square">
            <FwdcoGlobe usLatinoLabel={usLatinoLabel} />
          </div>
        </motion.div>

        {/* Two cards with image headers */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: "easeOut", delay: 0.15 }}
          className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-5"
          data-testid="location-blocks"
        >
          {t.globe.blocks.map((b) => (
            <div
              key={b.title}
              className="overflow-hidden fwd-card-hover"
              style={{
                background: "#141417",
                border: "1px solid #2A2A2E",
                borderRadius: "2px",
              }}
              data-testid={`location-block-${b.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              {/* Image header — 200px tall with dark gradient overlay */}
              <div className="relative" style={{ height: "200px" }}>
                <img
                  src={b.image}
                  alt={b.title}
                  className="w-full h-full"
                  style={{
                    objectFit: "cover",
                    objectPosition: b.imagePosition || "center",
                  }}
                  loading="lazy"
                />
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(to bottom, rgba(10,10,11,0) 40%, rgba(10,10,11,0.85) 90%, #0A0A0B 100%)",
                  }}
                />
              </div>

              {/* Card content */}
              <div className="p-8 md:p-10">
                <h3 className="fwd-heading mb-4">{b.title}</h3>
                <p className="fwd-body">{b.description}</p>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

function FwdcoGlobe({ usLatinoLabel }) {
  return (
    <svg
      viewBox="0 0 800 800"
      xmlns="http://www.w3.org/2000/svg"
      className="w-full h-full fwdco-globe"
      aria-label="FWDCO Group primary locations globe"
    >
      <defs>
        <radialGradient id="fg-bg" cx="40%" cy="36%" r="60%">
          <stop offset="0%" stopColor="#221a07" />
          <stop offset="55%" stopColor="#120e04" />
          <stop offset="100%" stopColor="#060402" />
        </radialGradient>
        <linearGradient id="fg-land" x1="25%" y1="10%" x2="75%" y2="90%">
          <stop offset="0%" stopColor="#DDB84E" />
          <stop offset="45%" stopColor="#C49838" />
          <stop offset="100%" stopColor="#9A7A22" />
        </linearGradient>
        <linearGradient id="fg-ring" x1="10%" y1="10%" x2="90%" y2="90%">
          <stop offset="0%" stopColor="#EAC860" />
          <stop offset="35%" stopColor="#C29838" />
          <stop offset="65%" stopColor="#8C6C18" />
          <stop offset="100%" stopColor="#D6AC44" />
        </linearGradient>
        <radialGradient id="fg-vig" cx="50%" cy="50%" r="50%">
          <stop offset="55%" stopColor="transparent" />
          <stop offset="100%" stopColor="#040301" stopOpacity=".75" />
        </radialGradient>
        <filter id="fg-rGlow">
          <feGaussianBlur stdDeviation="4" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <filter id="fg-pGlow">
          <feGaussianBlur stdDeviation="3.5" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <filter id="fg-hGlow">
          <feGaussianBlur stdDeviation="6" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <filter id="fg-lDepth">
          <feDropShadow dx="-3" dy="4" stdDeviation="5" floodColor="#000" floodOpacity=".5" />
        </filter>
        <clipPath id="fg-gc">
          <circle cx="400" cy="400" r="362" />
        </clipPath>
      </defs>

      <circle cx="400" cy="400" r="388" fill="none" stroke="url(#fg-ring)" strokeWidth=".4" opacity=".25" />
      <circle cx="400" cy="400" r="380" fill="none" stroke="url(#fg-ring)" strokeWidth=".4" opacity=".18" />
      <circle cx="400" cy="400" r="369" fill="none" stroke="url(#fg-ring)" strokeWidth="7" filter="url(#fg-rGlow)" />
      <circle cx="400" cy="400" r="362" fill="url(#fg-bg)" />

      <g clipPath="url(#fg-gc)" opacity=".05" stroke="#C49838" strokeWidth=".7" fill="none">
        <ellipse cx="400" cy="400" rx="362" ry="58" />
        <ellipse cx="400" cy="330" rx="340" ry="54" />
        <ellipse cx="400" cy="470" rx="340" ry="54" />
        <ellipse cx="400" cy="400" rx="60" ry="362" />
        <ellipse cx="400" cy="400" rx="160" ry="362" />
        <ellipse cx="400" cy="400" rx="260" ry="362" />
      </g>

      <g clipPath="url(#fg-gc)" filter="url(#fg-lDepth)">
        <path fill="url(#fg-land)" opacity=".97" d="M295.3,80.3L297.9,85.3L272.1,84.1L254.5,91.2L242.0,102.3L232.6,109.2L235.3,121.2L234.0,125.9L263.6,128.1L265.8,137.6L263.8,151.3L270.9,171.6L263.3,185.8L256.0,200.6L248.9,227.0L253.3,238.6L266.0,256.9L266.4,274.3L272.4,292.4L290.0,311.5L310.0,324.6L314.4,336.9L324.7,349.5L364.6,362.9L388.1,375.4L406.0,381.6L424.1,393.7L436.2,399.7L478.4,410.7L478.2,404.6L483.9,398.2L501.0,391.2L442.2,399.6L436.0,387.4L411.9,369.3L405.9,363.2L417.2,332.8L400.0,332.8L405.7,326.8L417.2,332.8L360.2,326.4L361.1,308.5L361.8,296.7L373.2,285.2L405.3,279.6L410.6,279.6L426.6,279.4L432.2,285.1L455.4,308.1L444.8,314.3L444.0,302.4L437.9,290.8L447.7,279.0L451.8,267.4L469.8,249.8L471.8,233.1L474.3,222.2L482.1,216.4L489.5,210.7L498.9,199.7L503.3,189.1L509.0,183.6L544.5,179.8L529.8,166.6L521.2,158.0L490.0,138.7L477.2,127.3L472.4,119.7L454.4,128.9L433.2,118.3L413.9,118.8L394.2,122.6L384.7,130.2L383.3,142.4L406.7,142.5L426.7,142.2L428.1,150.8L437.6,163.9L439.3,173.1L420.9,188.0L425.6,192.9L446.7,192.3L461.3,201.9L509.0,183.6L492.0,170.3L481.2,161.7L463.9,153.7L438.8,137.7L434.4,122.0L415.5,111.5L400.0,118.9L384.7,130.2L334.4,140.6L301.3,137.8L277.6,130.5L263.6,128.1L261.3,118.6L256.9,108.3L252.4,96.3L254.5,91.2L272.1,84.1L295.3,80.3Z" />
        <path fill="url(#fg-land)" opacity=".97" d="M506.4,384.8L517.8,384.1L556.9,387.3L562.7,393.0L568.6,398.6L563.6,405.2L569.4,410.8L569.7,416.9L590.7,414.7L606.1,419.0L616.2,423.9L626.1,434.7L626.3,446.8L635.5,445.4L661.2,459.0L687.2,465.1L686.2,477.0L676.8,496.4L671.3,508.9L666.5,515.5L663.0,532.5L653.4,555.5L646.3,567.0L637.0,573.8L623.4,581.2L609.9,597.9L591.8,614.5L583.8,624.5L575.6,634.3L561.2,640.3L551.1,653.8L522.4,664.5L514.7,672.8L507.0,680.7L499.5,688.2L495.8,695.0L484.8,702.0L481.2,708.1L477.5,713.8L478.9,716.2L485.3,715.7L490.7,712.7L511.6,705.4L523.2,698.5L464.9,696.9L463.3,690.4L469.8,683.1L476.5,675.4L483.3,667.4L481.1,659.5L488.0,650.8L496.1,637.3L499.2,623.4L502.1,608.9L510.8,588.3L513.1,572.5L504.0,556.9L482.3,547.0L477.5,530.3L472.1,519.1L460.5,507.9L454.9,490.4L461.0,478.3L461.1,460.2L473.2,459.8L473.2,453.7L479.1,441.3L478.9,429.1L478.6,416.9L478.4,410.7L495.6,397.7L495.2,391.5L506.4,384.8Z" />
        <path fill="url(#fg-land)" opacity=".88" d="M437.2,59.7L446.6,59.4L458.1,61.9L474.9,66.7L490.6,72.8L510.0,82.8L516.4,89.3L517.2,101.7L526.6,121.0L517.8,122.5L501.7,116.5L491.6,106.2L477.5,97.1L462.4,85.8L445.1,76.2L421.4,73.1L428.7,68.0L432.8,63.0L432.9,60.7L437.2,59.7Z" />
      </g>

      <path className="fg-conn" stroke="#D4A843" strokeWidth="1.6" fill="none" opacity=".85" d="M293.0,264.7L347.5,341.8L496.1,430.2L497.0,639.4L533.7,647.4" />
      <path className="fg-conn" stroke="#fff" strokeWidth="1.2" fill="none" opacity=".55" style={{ animationDelay: ".9s" }} d="M365.0,263.2L293.0,264.7" />

      <g>
        <g>
          <circle cx="364.97" cy="263.23" r="8" className="fg-ring-hq1" />
          <circle cx="364.97" cy="263.23" r="8" className="fg-ring-hq2" />
          <circle cx="364.97" cy="263.23" r="10" fill="rgba(255,255,255,.12)" filter="url(#fg-hGlow)" />
          <circle cx="364.97" cy="263.23" r="5" fill="#ffffff" filter="url(#fg-hGlow)" />
          <circle cx="364.97" cy="263.23" r="2.5" fill="#fffff8" />
          <text x="378.97" y="267.23" textAnchor="start" className="fg-lbl fg-lbl-hq">DALLAS</text>
        </g>
        <Pin cx={293.02} cy={264.7} label={usLatinoLabel} anchor="end" dx={-14} />
        <Pin cx={347.49} cy={341.78} label="MÉXICO" anchor="end" dx={-14} />
        <Pin cx={460} cy={398} label="PANAMÁ" anchor="start" dx={16} />
        <Pin cx={496.11} cy={430.18} label="COLOMBIA" anchor="end" dx={-14} />
        <text x="642.54" y="531.94" textAnchor="start" className="fg-lbl fg-lbl-br">BRASIL</text>
        <Pin cx={497.02} cy={639.44} label="CHILE" anchor="end" dx={-14} />
        <Pin cx={533.69} cy={647.42} label="ARGENTINA" anchor="start" dx={14} />
      </g>

      <circle cx="400" cy="400" r="362" fill="url(#fg-vig)" clipPath="url(#fg-gc)" />
      <circle cx="400" cy="400" r="362" fill="none" stroke="url(#fg-ring)" strokeWidth=".8" opacity=".5" />
    </svg>
  );
}

function Pin({ cx, cy, label, anchor, dx }) {
  return (
    <g>
      <circle cx={cx} cy={cy} r="6" className="fg-ring1" />
      <circle cx={cx} cy={cy} r="6" className="fg-ring2" />
      <circle cx={cx} cy={cy} r="8" fill="rgba(212,168,67,.18)" filter="url(#fg-pGlow)" />
      <circle cx={cx} cy={cy} r="3.5" fill="#D4A843" filter="url(#fg-pGlow)" />
      <circle cx={cx} cy={cy} r="1.8" fill="#F2D870" />
      <text x={cx + dx} y={cy + 4} textAnchor={anchor} className="fg-lbl fg-lbl-mkt">
        {label}
      </text>
    </g>
  );
}
